import type { ProjectSuccessAnalysis, ProjectSuccessLayerGenealogy, ProjectSuccessLayerInfo } from '#types/types';
import type { InternalAssetRequesterContext } from '#lib/server/internalAssetFetcher';

type AssetDataUriMapInput =
  | ReadonlyMap<string, string>
  | Map<string, string>
  | Record<string, string>
  | Array<readonly [string, string]>;
interface AssetResolverArgs {
  url: string;
  absoluteUrl: string | null;
  ownerCandidates: readonly string[];
  requester?: InternalAssetRequesterContext | null;
}

type AssetResolver = (args: AssetResolverArgs) => Promise<string | null>;

export interface BuildReportHtmlParams {
  projectName: string;
  analysis: ProjectSuccessAnalysis;
  generatedAtIso: string;
  assetOwnerCandidates?: readonly string[] | string[];
  requester?: InternalAssetRequesterContext | null;
  inlineImages?: boolean;
  assetDataUriMap?: AssetDataUriMapInput | null;
  assetResolver?: AssetResolver | null;
}

interface UrlToBase64Options {
  ownerCandidates: string[];
  requester?: InternalAssetRequesterContext | null;
  assetDataUriMap: ReadonlyMap<string, string> | null;
  assetResolver?: AssetResolver | null;
}

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const formatTimestamp = (value: number | string | null | undefined): string => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return new Date(value).toLocaleString();
  }
  if (typeof value === 'string') {
    const parsed = Date.parse(value);
    if (!Number.isNaN(parsed)) {
      return new Date(parsed).toLocaleString();
    }
    return value;
  }
  return 'Unknown';
};

const normalizeBaseUrl = (value: string | undefined | null): string | null => {
  if (!value) {
    return null;
  }
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }
  const withProtocol = /^[a-z][a-z\d+.-]*:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  const sanitized = withProtocol.replace(/\/+$/, '');
  return `${sanitized}/`;
};

const SITE_BASE_URL = normalizeBaseUrl(process.env.NEXT_PUBLIC_SITE_URL);

const ABSOLUTE_URL_PATTERN = /^[a-z][a-z\d+.-]*:\/\//i;

export const resolveAbsoluteUrl = (input: string): string | null => {
  const trimmed = input.trim();
  if (!trimmed) {
    return null;
  }
  if (ABSOLUTE_URL_PATTERN.test(trimmed)) {
    return trimmed;
  }

  if (typeof window !== 'undefined' && window.location?.origin) {
    try {
      return new URL(trimmed, window.location.origin).href;
    } catch (error) {
      // Fallback to SITE_BASE_URL logic if window resolution fails
    }
  }

  if (!SITE_BASE_URL) {
    console.warn(
      `[htmlGenerator] Unable to inline relative asset because NEXT_PUBLIC_SITE_URL (or fallback) is not configured. Skipping: ${trimmed}`,
    );
    return null;
  }
  try {
    return new URL(trimmed, SITE_BASE_URL).href;
  } catch (error) {
    console.error(`[htmlGenerator] Failed to resolve absolute URL for ${trimmed} using base ${SITE_BASE_URL}`, error);
    return null;
  }
};

const normalizeAssetMap = (input: AssetDataUriMapInput | null | undefined): ReadonlyMap<string, string> | null => {
  if (!input) {
    return null;
  }

  const upsert = (map: Map<string, string>, key: string, value: string) => {
    const trimmedKey = key.trim();
    if (!trimmedKey || !value) {
      return;
    }
    map.set(trimmedKey, value);
    const absolute = resolveAbsoluteUrl(trimmedKey);
    if (absolute && absolute !== trimmedKey) {
      map.set(absolute, value);
    }
  };

  const workingMap = new Map<string, string>();

  if (input instanceof Map) {
    for (const [key, value] of input.entries()) {
      if (typeof key === 'string' && typeof value === 'string') {
        upsert(workingMap, key, value);
      }
    }
    return workingMap;
  }

  if (Array.isArray(input)) {
    for (const tuple of input) {
      if (!Array.isArray(tuple) || tuple.length < 2) {
        continue;
      }
      const [key, value] = tuple;
      if (typeof key === 'string' && typeof value === 'string') {
        upsert(workingMap, key, value);
      }
    }
    return workingMap;
  }

  if (typeof (input as ReadonlyMap<unknown, unknown>).forEach === 'function') {
    (input as ReadonlyMap<unknown, unknown>).forEach((value, key) => {
      if (typeof key === 'string' && typeof value === 'string') {
        upsert(workingMap, key, value);
      }
    });
    return workingMap;
  }

  const entries = Object.entries(input);
  for (const [key, value] of entries) {
    if (typeof value === 'string') {
      upsert(workingMap, key, value);
    }
  }
  return workingMap;
};

const arrayBufferToBase64 = (arrayBuffer: ArrayBuffer): string => {
  if (typeof Buffer !== 'undefined') {
    return Buffer.from(arrayBuffer).toString('base64');
  }
  let binary = '';
  const bytes = new Uint8Array(arrayBuffer);
  const chunkSize = 0x8000;
  for (let offset = 0; offset < bytes.length; offset += chunkSize) {
    const chunk = bytes.subarray(offset, offset + chunkSize);
    binary += String.fromCharCode(...chunk);
  }
  if (typeof btoa === 'function') {
    return btoa(binary);
  }
  throw new Error('Cannot convert ArrayBuffer to base64: no Buffer or btoa implementation available.');
};

const getDataUriFromMap = (
  map: ReadonlyMap<string, string> | null,
  key: string,
): string | null => {
  if (!map) {
    return null;
  }
  const direct = map.get(key);
  if (typeof direct === 'string' && direct.trim()) {
    return direct;
  }
  return null;
};

const urlToBase64 = async (url: string, options: UrlToBase64Options): Promise<string> => {
  const trimmedUrl = url.trim();
  if (trimmedUrl.startsWith('data:')) {
    return trimmedUrl;
  }

  const fromMap = getDataUriFromMap(options.assetDataUriMap, trimmedUrl);
  if (fromMap) {
    return fromMap;
  }
  console.warn(`[htmlGenerator] Asset not found in map: "${trimmedUrl}". Attempting fallback fetch.`);

  const absoluteUrl = resolveAbsoluteUrl(trimmedUrl);
  if (absoluteUrl) {
    const absoluteFromMap = getDataUriFromMap(options.assetDataUriMap, absoluteUrl);
    if (absoluteFromMap) {
      return absoluteFromMap;
    }
  }

  if (options.assetResolver) {
    const resolved = await options.assetResolver({
      url: trimmedUrl,
      absoluteUrl,
      ownerCandidates: options.ownerCandidates,
      requester: options.requester,
    });
    if (resolved && resolved.trim()) {
      return resolved;
    }
  }

  try {
    const resolvedUrl = absoluteUrl ?? trimmedUrl;
    const response = await fetch(resolvedUrl);
    if (!response.ok) {
      console.error(
        `[htmlGenerator] Fallback fetch failed: ${response.status} ${response.statusText} for URL: ${resolvedUrl}`,
      );
      return trimmedUrl;
    }
    const buffer = await response.arrayBuffer();
    const base64 = arrayBufferToBase64(buffer);
    const contentType = response.headers.get('content-type') ?? 'image/webp';
    return `data:${contentType};base64,${base64}`;
  } catch (error) {
    const resolvedUrl = absoluteUrl ?? trimmedUrl;
    console.error(`[htmlGenerator] Exception during fallback fetch/conversion: ${resolvedUrl}`, error);
    return trimmedUrl;
  }
};

const HIDDEN_PROMPT_SUFFIX =
  'The last image contains a red #FF0000 raw drawing. You must replace this drawing. Use the shape of the drawing as a strong visual guide. All other pixels must be perfectly preserved.';

const stripHiddenPromptSuffix = (value: string): string => {
  const trimmed = value.trimEnd();
  if (trimmed.endsWith(HIDDEN_PROMPT_SUFFIX)) {
    const withoutSuffix = trimmed.slice(0, trimmed.length - HIDDEN_PROMPT_SUFFIX.length);
    return stripHiddenPromptSuffix(withoutSuffix);
  }
  return trimmed;
};

const renderLayerPreview = async (
  layer: ProjectSuccessLayerInfo,
  inlineImages: boolean,
  inlineContext: UrlToBase64Options,
  isFirstLayer: boolean,
) => {
  const friendlyName = layer.name ?? `Layer ${layer.id}`;
  
  const sanitizeUrl = (value?: string | null): string | null => {
    if (typeof value === 'string') {
      const trimmed = value.trim();
      return trimmed.length > 0 ? trimmed : null;
    }
    return null;
  };

  const resolveInputUrl = () => {
    if (isFirstLayer) return null; // Force single view for the first layer (work source)
    
    const candidates: Array<string | null | undefined> = [
      layer.reconstructedInputUrl,
      layer.generation?.inputAssets?.main,
      layer.generation?.inputAssets?.control,
    ];
    for (const candidate of candidates) {
      const sanitized = sanitizeUrl(candidate);
      if (sanitized) {
        return sanitized;
      }
    }
    return null;
  };

  const inputUrl = resolveInputUrl();
  const resultUrl = sanitizeUrl(layer.imageSrc);

  // No logging by default; instrumentation added only when debugging specific layers.

  const renderPane = async (
    label: string,
    url: string | null | undefined,
    altDescription: string,
    fallback: string,
    extraClass: string,
  ) => {
    const safeLabel = escapeHtml(label);
    const classNames = ['figure', 'layer-preview-pane', extraClass];
    if (!url) {
      classNames.push('is-empty');
    }
    const classAttr = classNames.filter(Boolean).join(' ');

    const processedUrl =
      url && inlineImages ? await urlToBase64(url, inlineContext) : url;

    if (processedUrl) {
      return `<figure class="${classAttr}">
        <img src="${escapeHtml(processedUrl)}" alt="${escapeHtml(altDescription)}" loading="lazy" />
        <figcaption>${safeLabel}</figcaption>
      </figure>`;
    }

    return `<figure class="${classAttr}">
      <div class="preview-empty">${escapeHtml(fallback)}</div>
      <figcaption>${safeLabel}</figcaption>
    </figure>`;
  };

  if (inputUrl) {
    const inputPane = await renderPane('Input', inputUrl, `${friendlyName} input`, 'Input unavailable.', 'input');
    const resultPane = await renderPane('Result', resultUrl, `${friendlyName} result`, 'Result unavailable.', 'result');
    return `<div class="layer-preview">
      ${inputPane}
      ${resultPane}
    </div>`;
  }

  if (resultUrl) {
    const processedUrl = inlineImages ? await urlToBase64(resultUrl, inlineContext) : resultUrl;
    return `<figure class="figure layer-single">
      <img src="${escapeHtml(processedUrl)}" alt="${escapeHtml(`${friendlyName} result`)}" loading="lazy" />
      <figcaption>Result</figcaption>
    </figure>`;
  }

  return `<div class="placeholder">Layer preview unavailable.</div>`;
};

const renderGenerationMetadata = async (
  layer: ProjectSuccessLayerInfo,
  inlineImages: boolean,
  inlineContext: UrlToBase64Options,
) => {
  const primaryModel =
    typeof layer.source === 'string' && layer.source.trim().length > 0 ? layer.source.trim() : null;
  const fallbackModel =
    layer.generation.model && layer.generation.model.trim().length > 0 ? layer.generation.model.trim() : null;
  const modelLabel = escapeHtml(primaryModel ?? fallbackModel ?? 'Unknown model');
  const promptSource = typeof layer.generation.prompt === 'string' ? layer.generation.prompt : null;
  const promptCandidate = promptSource ? stripHiddenPromptSuffix(promptSource) : null;
  const promptNormalized = promptCandidate && promptCandidate.trim().length > 0 ? promptCandidate.trim() : null;
  const promptValue = promptNormalized ? escapeHtml(promptNormalized).replace(/\n/g, '<br />') : null;

  const promptBlock = promptValue
    ? `<div class="meta-row prompt-block">
        <dt>Prompt</dt>
        <dd class="prompt-content">${promptValue}</dd>
      </div>`
    : '';

  let referencesBlock = '';
  const references = layer.generation.inputAssets?.references;
  if (Array.isArray(references) && references.length > 0) {
    const validRefs = references.filter((r): r is string => typeof r === 'string' && r.trim().length > 0);
    if (validRefs.length > 0) {
      const images = await Promise.all(
        validRefs.map(async (url) => {
          const src = inlineImages ? await urlToBase64(url, inlineContext) : url;
          return `<img src="${escapeHtml(src)}" loading="lazy" alt="Reference" />`;
        }),
      );
      referencesBlock = `<div class="meta-row references-block">
        <dt>References</dt>
        <dd class="references-grid">
          ${images.join('')}
        </dd>
      </div>`;
    }
  }

  return `<dl class="meta">
    <div class="meta-row">
      <dt>Source</dt>
      <dd>${modelLabel}</dd>
    </div>
    ${promptBlock}
    ${referencesBlock}
  </dl>`;
};

const renderLayerCard = async (
  genealogy: ProjectSuccessLayerGenealogy,
  index: number,
  inlineImages: boolean,
  inlineContext: UrlToBase64Options,
) => {
  const layer = genealogy.layer;
  const isFirstLayer = index === 0;
  const finalTimestamp = formatTimestamp(layer.timestamp ?? null);
  const modificationLog = Array.isArray(layer.modificationLog) ? layer.modificationLog : [];
  const formattedLog = modificationLog
    .map((entry) => {
      const labelMap: Record<string, string> = {
        MANUAL_MOVE: 'Moved manually',
        MANUAL_RESIZE: 'Resized manually',
        MANUAL_ALIGN: 'Aligned to underlying',
        MANUAL_EXTRACT: 'Auto cut',
        MANUAL_FEATHER: 'Feathered edges',
        MANUAL_HARMONIZE: 'Harmonized color',
        MANUAL_UNCHANGED_HARMONIZE: 'Harmonized color drift',
        MERGED_INTO: 'Merged into another layer',
        CREATED_FROM_MERGE: 'Created from merge',
      };
      const label = labelMap[entry.type] ?? entry.type;
      const timestamp = entry.timestamp ? formatTimestamp(entry.timestamp) : null;
      const description = entry.description ? ` — ${escapeHtml(entry.description)}` : '';
      const timePart = timestamp && timestamp !== 'Unknown' ? `<span class="history-time">${escapeHtml(timestamp)}</span>` : '';
      return `<li>${timePart}<span class="history-label">${escapeHtml(label)}</span>${description}</li>`;
    })
    .join('');
  const historyBlock = formattedLog
    ? `<section class="layer-history">
        <h5>Modification History</h5>
        <ul>${formattedLog}</ul>
      </section>`
    : '';

  return `<article class="layer-card">
    ${await renderLayerPreview(layer, inlineImages, inlineContext, isFirstLayer)}
    <div class="layer-info">
      <header class="layer-header">
        <h4>Layer ${index + 1}: ${escapeHtml(layer.name ?? `Layer ${layer.id}`)}</h4>
        <span class="timestamp">Last updated: ${finalTimestamp}</span>
      </header>
      ${await renderGenerationMetadata(layer, inlineImages, inlineContext)}
      ${historyBlock}
    </div>
  </article>`;
};

const renderWorkspaceSection = async (
  workspace: ProjectSuccessAnalysis['analyzedWorkspaces'][number],
  index: number,
  inlineImages: boolean,
  inlineContext: UrlToBase64Options,
) => {
  const layerCount = workspace.layerGenealogies.length;
  const layersHtml = layerCount
    ? (
        await Promise.all(
          workspace.layerGenealogies.map((layer, layerIndex) =>
            renderLayerCard(layer, layerIndex, inlineImages, inlineContext),
          ),
        )
      ).join('\n')
    : '<p class="placeholder">No visible layers were recorded in the validated version.</p>';

  return `<section class="workspace-section">
    <header class="workspace-header">
      <h3>Workspace ${index + 1}: ${escapeHtml(workspace.workspaceName)}</h3>
      <span class="badge">${layerCount} visible layer${layerCount === 1 ? '' : 's'}</span>
    </header>
    <p class="workspace-context">
      This timeline shows the chronological progression, starting with the initial upload (when available) and each before/after generation together with the captured model and prompt.
    </p>
    <div class="layer-grid">
      ${layersHtml}
    </div>
  </section>`;
};

export const buildProjectReportHtml = async (params: BuildReportHtmlParams): Promise<string> => {
  const {
    projectName,
    analysis,
    generatedAtIso,
    assetOwnerCandidates,
    requester = null,
    inlineImages = true,
    assetDataUriMap = null,
    assetResolver = null,
  } = params;
  const generatedAt = formatTimestamp(generatedAtIso);

  const normalizedOwnerCandidates = Array.isArray(assetOwnerCandidates)
    ? assetOwnerCandidates
    : typeof assetOwnerCandidates === 'string'
      ? [assetOwnerCandidates]
      : [];

  const ownerCandidates = Array.from(
    new Set(
      normalizedOwnerCandidates
        .map((candidate) => (typeof candidate === 'string' ? candidate.trim() : ''))
        .filter((candidate) => candidate.length > 0),
    ),
  );

  const inlineContext: UrlToBase64Options = {
    ownerCandidates,
    requester,
    assetDataUriMap: normalizeAssetMap(assetDataUriMap),
    assetResolver,
  };

  const heroWorkspace = analysis.analyzedWorkspaces.at(-1) ?? null;
  const heroVersion = heroWorkspace?.validatedVersion ?? null;
  const initialVersion = heroWorkspace?.initialVersion ?? null;
  
  const heroTitle =
    typeof heroVersion?.name === 'string' && heroVersion.name.trim().length > 0 ? heroVersion.name.trim() : null;
  
  const heroImageUrl = heroVersion?.imageUrl ?? null;
  const initialImageUrl = initialVersion?.imageUrl ?? null;

  const processedHeroImageUrl =
    heroImageUrl && inlineImages ? await urlToBase64(heroImageUrl, inlineContext) : heroImageUrl;
  const processedInitialImageUrl =
    initialImageUrl && inlineImages ? await urlToBase64(initialImageUrl, inlineContext) : initialImageUrl;

  let heroSection = '';

  if (heroVersion && processedHeroImageUrl) {
    if (initialVersion && processedInitialImageUrl && processedInitialImageUrl !== processedHeroImageUrl) {
      // Before / After Layout
      heroSection = `<section class="hero">
        <div class="hero-comparison">
          <figure>
            <img src="${escapeHtml(processedInitialImageUrl)}" alt="Initial State" loading="lazy" />
            <figcaption>Before: ${escapeHtml(initialVersion.name || 'Initial State')}</figcaption>
          </figure>
          <figure>
            <img src="${escapeHtml(processedHeroImageUrl)}" alt="${escapeHtml(heroTitle ?? 'Final Result')}" loading="lazy" />
            <figcaption>After: ${escapeHtml(heroTitle ?? 'Final Result')}</figcaption>
          </figure>
        </div>
      </section>`;
    } else {
      // Single Image Layout
      const heroCaptionText = heroTitle
        ? `Final workspace view: ${heroTitle}`
        : 'Final workspace view (latest validated state)';
        
      heroSection = `<section class="hero">
          <figure>
            <img src="${escapeHtml(processedHeroImageUrl)}" alt="${escapeHtml(heroTitle ?? 'Final workspace view')}" loading="lazy" />
            <figcaption>${escapeHtml(heroCaptionText)}</figcaption>
          </figure>
        </section>`;
    }
  }
    
  const workspacesHtml = (
    await Promise.all(
      analysis.analyzedWorkspaces.map((workspace, index) =>
        renderWorkspaceSection(workspace, index, inlineImages, inlineContext),
      ),
    )
  ).join('\n');

  return `<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charSet="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <title>${escapeHtml(projectName)} – Layer Genealogy Report</title>
    <style>
      :root {
        color-scheme: dark;
        font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        background: #050505;
        color: #f4f4f4;
        --muted: rgba(200, 200, 200, 0.6);
        --accent: #d0d0d0;
        --border: rgba(255, 255, 255, 0.06);
        --radius: 18px;
        --story-image-max-height: min(78vh, 1100px);
        --story-image-max-height-mobile: 68vh;
      }
      body {
        margin: 0;
        padding: 0;
        background: linear-gradient(180deg, #050505 0%, #101010 100%);
      }
      .page {
        max-width: 80vw;
        width: 80vw;
        box-sizing: border-box;
        margin: 0 auto;
        padding: 48px 24px 96px;
      }
      .page-header {
        text-align: center;
        margin-bottom: 48px;
      }
      .page-header h1 {
        margin: 0 0 12px;
        font-size: clamp(2.4rem, 4vw, 3.2rem);
        letter-spacing: -0.04em;
      }
      .page-header .meta {
        margin: 0;
        color: var(--muted);
      }
      .hero {
        margin: 64px 0 0 0;
        width: 95vw;
        max-width: none;
        position: relative;
        left: 50%;
        transform: translateX(-50%);
      }
      .hero-comparison {
        display: flex;
        gap: 24px;
        width: 100%;
      }
      .hero-comparison figure {
        flex: 1;
        min-width: 0;
      }
      @media (max-width: 768px) {
        .hero-comparison {
          flex-direction: column;
        }
      }
      .hero figure {
        margin: 0;
        border-radius: var(--radius);
        overflow: hidden;
        border: 1px solid rgba(255, 255, 255, 0.08);
        box-shadow: 0 20px 60px rgba(0, 0, 0, 0.45);
      }
      .hero img {
        width: auto;
        max-width: 100%;
        height: auto;
        max-height: var(--story-image-max-height);
        object-fit: contain;
        margin: 0 auto;
        display: block;
        background: rgba(6, 6, 6, 0.9);
      }
      .hero figcaption {
        padding: 14px 16px;
        background: rgba(12, 12, 12, 0.9);
        color: var(--muted);
      }
      .figure {
        margin: 0;
        border-radius: 14px;
        overflow: hidden;
        border: 1px solid rgba(255, 255, 255, 0.08);
        background: rgba(12, 12, 12, 0.92);
      }
      .figure img {
        width: 100%;
        display: block;
      }
      .figure figcaption {
        padding: 10px 12px;
        background: rgba(12, 12, 12, 0.92);
        font-size: 0.85rem;
        color: var(--muted);
      }
      .workspace-section {
        margin-bottom: 48px;
        padding: 24px;
        border-radius: var(--radius);
        background: rgba(18, 18, 18, 0.92);
        border: 1px solid rgba(255, 255, 255, 0.06);
        box-shadow: 0 16px 40px rgba(0, 0, 0, 0.35);
      }
      .workspace-header {
        display: flex;
        justify-content: space-between;
        align-items: baseline;
        gap: 16px;
        margin-bottom: 24px;
      }
      .workspace-context {
        margin: 12px 0 24px;
        color: var(--muted);
        font-size: 0.95rem;
        line-height: 1.5;
      }
      .workspace-header h3 {
        margin: 0;
        font-size: 1.4rem;
      }
      .workspace-header .badge {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        background: rgba(120, 120, 120, 0.2);
        color: rgba(220, 220, 220, 0.9);
        border: 1px solid rgba(120, 120, 120, 0.35);
        border-radius: 999px;
        padding: 4px 12px;
        font-size: 0.8rem;
        text-transform: uppercase;
        letter-spacing: 0.08em;
      }
      .layer-grid {
        display: grid;
        gap: 160px;
      }
      .placeholder {
        padding: 18px;
        border-radius: 14px;
        border: 1px dashed var(--border);
        color: var(--muted);
        text-align: center;
        font-size: 0.9rem;
        background: rgba(15, 15, 15, 0.8);
      }
      .layer-card {
        background: rgba(24, 24, 24, 0.96);
        border-radius: calc(var(--radius) - 6px);
        border: 1px solid rgba(255, 255, 255, 0.07);
        padding: 24px;
        display: flex;
        flex-direction: column;
        gap: 24px;
        align-items: stretch;
        box-shadow: 0 18px 36px rgba(0, 0, 0, 0.45);
      }
      .layer-preview {
        display: flex;
        flex-wrap: wrap;
        gap: 20px;
        width: 100%;
      }
      .layer-preview-pane {
        flex: 1 1 320px;
        display: flex;
        flex-direction: column;
        min-width: 0;
        min-height: 280px;
      }
      .layer-preview-pane img {
        flex: 0 0 auto;
        width: auto;
        max-width: 100%;
        height: auto;
        max-height: var(--story-image-max-height);
        object-fit: contain;
        margin: 0 auto;
        background: rgba(12, 12, 12, 0.92);
      }
      .layer-preview-pane .preview-empty {
        flex: 1;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 24px;
        text-align: center;
        color: var(--muted);
        font-size: 0.9rem;
        background: rgba(12, 12, 12, 0.92);
      }
      .layer-preview-pane figcaption {
        border-top: 1px solid rgba(255, 255, 255, 0.08);
        color: rgba(220, 220, 220, 0.85);
      }
      .layer-single figcaption {
        border-top: 1px solid rgba(255, 255, 255, 0.08);
        color: rgba(220, 220, 220, 0.85);
      }
      .layer-single img {
        width: auto;
        max-width: 100%;
        height: auto;
        max-height: var(--story-image-max-height);
        object-fit: contain;
        margin: 0 auto;
        background: rgba(12, 12, 12, 0.92);
      }
      .layer-info {
        width: 100%;
        display: flex;
        flex-direction: column;
        gap: 18px;
      }
      .layer-history {
        display: flex;
        flex-direction: column;
        gap: 10px;
        padding: 14px 16px;
        border: 1px solid rgba(255, 255, 255, 0.08);
        border-radius: 12px;
        background: rgba(18, 18, 18, 0.9);
      }
      .layer-history h5 {
        margin: 0;
        font-size: 0.9rem;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        color: rgba(200, 200, 200, 0.8);
      }
      .layer-history ul {
        margin: 0;
        padding-left: 18px;
        display: flex;
        flex-direction: column;
        gap: 6px;
        color: var(--muted);
        font-size: 0.88rem;
      }
      .layer-history li {
        list-style: disc;
        line-height: 1.4;
      }
      .history-time {
        display: inline-block;
        margin-right: 8px;
        color: rgba(220, 220, 220, 0.75);
        font-size: 0.78rem;
        letter-spacing: 0.05em;
        text-transform: uppercase;
      }
      .history-label {
        font-weight: 500;
        color: #f4f4f4;
        margin-right: 4px;
      }
      .layer-header {
        display: flex;
        justify-content: space-between;
        gap: 12px;
        flex-wrap: wrap;
        align-items: baseline;
      }
      .layer-header h4 {
        margin: 0;
        font-size: 1.2rem;
      }
      .layer-header .timestamp {
        color: var(--muted);
        font-size: 0.85rem;
      }
      .meta {
        margin: 0;
        padding: 0;
        display: flex;
        flex-direction: column;
        gap: 14px;
      }
      .meta-row {
        display: flex;
        flex-direction: column;
        gap: 6px;
      }
      .meta-row dt {
        font-size: 0.72rem;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        color: rgba(185, 185, 185, 0.85);
      }
      .meta-row dd {
        margin: 0;
        font-size: 0.95rem;
        color: #f3f3f3;
        line-height: 1.45;
      }
      .prompt-content {
        padding: 10px 12px;
        background: rgba(15, 15, 15, 0.95);
        border-radius: 10px;
        border: 1px solid rgba(255, 255, 255, 0.08);
        max-height: 200px;
        overflow: auto;
      }
      .prompt-content::-webkit-scrollbar {
        width: 6px;
      }
      .prompt-content::-webkit-scrollbar-thumb {
        background: rgba(140, 140, 140, 0.4);
        border-radius: 999px;
      }
      .references-block {
        margin-top: 12px;
      }
      .references-grid {
        display: flex;
        gap: 12px;
        width: 100%;
      }
      .references-grid img {
        flex: 1;
        min-width: 0;
        width: 100%;
        height: auto;
        max-height: 50vh;
        object-fit: contain;
        border-radius: 6px;
        border: 1px solid rgba(255, 255, 255, 0.1);
        background: rgba(0, 0, 0, 0.2);
      }
      footer.page-footer {
        margin-top: 96px;
        text-align: center;
        color: var(--muted);
        font-size: 0.85rem;
      }
      footer.page-footer strong {
        color: var(--accent);
      }
      @media (max-width: 768px) {
        .page {
          padding: 36px 14px 72px;
        }
        .hero img,
        .layer-preview-pane img,
        .layer-single img {
          max-height: var(--story-image-max-height-mobile);
        }
      }
    </style>
  </head>
  <body>
    <div class="page">
      <header class="page-header">
        <h1>${escapeHtml(projectName)}</h1>
        <p class="meta">Layer genealogy report generated on ${generatedAt}</p>
      </header>
      ${workspacesHtml || '<p class="placeholder">No validated progress was detected for this project.</p>'}
      ${heroSection}
      <footer class="page-footer">
        Crafted automatically by <strong>Fater Project Success Analysis</strong> - ${generatedAt}
      </footer>
    </div>
  </body>
</html>`;
};
